import React, { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useRoute } from '@react-navigation/native';
import { styled } from 'dripsy';

import { HubNavigatorRoutePropType } from '@navigation';
import { parseApiError } from '@utils';
import { ToastService } from '@services';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { useItemsModel } from '@models/Items/ItemsModel';
import { ListLayout, BaseText, Separator } from '@components';
import { ItemsList } from './components/ItemsList';
import { SectionItemListTypeEnum } from '@constants';

const ItemsCount = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

type RoutePropType = HubNavigatorRoutePropType<'Items'>;

export const ItemsScreen = observer(() => {
  const { t } = useTranslation();
  const route = useRoute<RoutePropType>();
  const items = useItemsModel();
  const list = items.listsMap.get(route.params.type, route.params.id);
  const title = t('items.title', {
    context: route.params.type,
    title: route.params.name,
  });

  useNavOptionsSet({ title });

  useAsyncInit(async () => {
    try {
      await list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  async function refresh() {
    try {
      await list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const isShowCount =
    route.params.type === SectionItemListTypeEnum.Tag;

  return (
    <ListLayout title={title}>
      <ItemsList
        ListHeaderComponent={() =>
          isShowCount ? (
            <Fragment>
              <ItemsCount>
                {t('items.result', {
                  count: list.count,
                })}
              </ItemsCount>
              <Separator size="$6" />
            </Fragment>
          ) : null
        }
        list={list}
        refresh={refresh}
        fetchMore={fetchMore}
      />
    </ListLayout>
  );
});
